import { useMemo } from "react";

// Use the same margin constants as ChartWrapper
const CANVAS_MARGIN = { left: 0, right: 55, top: 0, bottom: 30 }; // ✅ Reduced from 60px to 30px

export const useChartLayout = (height, charts, sectionHeights = null) =>
  useMemo(() => {
    const indicatorCharts = charts.filter((c) => c.type === "indicator");

    // Define spacing between chart sections
    const sectionSpacing = 0; // No gap between sections
    const totalSpacing =
      indicatorCharts.length > 0 ? sectionSpacing * indicatorCharts.length : 0;

    // ✅ FIX: Use same calculation as ChartWrapper resize logic
    const available =
      height - CANVAS_MARGIN.top - CANVAS_MARGIN.bottom - totalSpacing;

    let mainRatio, indicatorRatio;

    // ✅ SOLUTION: Check if we need to override ratios due to minimum height constraints
    const minChartHeight = 30; // Reduced minimum height per chart to allow more flexibility
    const totalMinHeightNeeded = charts.length * minChartHeight;
    const needsMinHeightOverride = available < totalMinHeightNeeded;

    if (needsMinHeightOverride && !sectionHeights) {
      // Only force equal distribution when space is too tight AND no custom heights are set
      const equalRatio = 1 / charts.length;
      mainRatio = equalRatio;
      indicatorRatio = equalRatio;
    } else if (sectionHeights) {
      // Custom heights are already proportional (should sum to 1.0)
      // No additional calculation needed, heights will be applied per section
    } else {
      // Calculate default proportional ratios based on number of charts
      const totalCharts = charts.length;

      if (totalCharts === 1) {
        // Only main chart
        mainRatio = 1;
        indicatorRatio = 0;
      } else if (totalCharts === 2) {
        // Main: 3/4, Indicator: 1/4
        mainRatio = 3 / 4;
        indicatorRatio = 1 / 4;
      } else {
        // Main: 2/4, Each indicator: 1/4 divided by number of indicators
        mainRatio = 2 / 4;
        indicatorRatio = 2 / 4 / indicatorCharts.length;
      }
    }

    const sorted = [...charts].sort((a, b) => a.index - b.index);
    let currentOrigin = 0;

    return sorted.map((chart, index) => {
      let chartHeight;

      if (needsMinHeightOverride && sectionHeights) {
        // When space is constrained, still try to respect custom heights but ensure minimum
        const sectionRatio = sectionHeights[chart.index] || 0.2; // fallback ratio
        const customHeight = Math.round(available * sectionRatio);
        chartHeight = Math.max(customHeight, minChartHeight);
      } else if (sectionHeights) {
        // Use custom height for this section
        const sectionRatio = sectionHeights[chart.index] || 0.2; // fallback ratio
        chartHeight = Math.round(available * sectionRatio);
      } else {
        // Use default ratio-based height
        chartHeight =
          chart.type === "main"
            ? Math.round(available * mainRatio)
            : Math.round(available * indicatorRatio);
      }

      // ✅ SAFETY: Ensure minimum height (should not be needed with override logic)
      const minSafeHeight = 50;
      if (chartHeight < minSafeHeight) {
        chartHeight = minSafeHeight;
      }

      const config = {
        ...chart,
        height: chartHeight,
        origin: [0, currentOrigin],
      };

      // Add spacing after each chart section (except the last one)
      if (index < sorted.length - 1) {
        currentOrigin += chartHeight + sectionSpacing;
      } else {
        currentOrigin += chartHeight;
      }

      return config;
    });
  }, [height, charts, sectionHeights]);
